<template>
  <figure class="blast-graphic mb-3">
    <figcaption class="text-left mb-1">
      <b>Distribution of hits on the query</b>
      <span v-if="query.title"> ({{ query.title }})</span>
      <span v-if="query.totalHits > shownHits.length" class="small ml-2">
        top {{ shownHits.length }} of {{ query.totalHits }} hits shown
      </span>
    </figcaption>
    <p v-if="!query.hits.length" class="text-left">No hits found.</p>
    <svg
      v-else
      :viewBox="`0 0 ${WIDTH} ${height}`"
      :width="WIDTH"
      class="mw-100"
      role="group"
      :aria-label="`Hits on the ${
        query.length || ''
      } residue query, coloured by alignment score`"
    >
      <!-- score key -->
      <g :transform="`translate(${PLOT_X}, 0)`">
        <text x="0" y="11" class="axis-label">Alignment score (bits):</text>
        <g
          v-for="(bin, i) in SCORE_BINS"
          :key="bin.label"
          :transform="`translate(${150 + i * 82}, 0)`"
        >
          <rect width="14" height="12" :fill="bin.color" />
          <text x="18" y="11" class="axis-label">{{ bin.label }}</text>
        </g>
      </g>

      <!-- query ruler -->
      <g :transform="`translate(0, ${RULER_Y})`">
        <text x="0" y="12" class="axis-label">Query</text>
        <rect :x="PLOT_X" y="4" :width="PLOT_WIDTH" height="8" fill="#930" />
        <g
          v-for="tick in ticks"
          :key="tick"
          :transform="`translate(${x(tick)}, 14)`"
        >
          <line y1="0" y2="5" stroke="#333" />
          <text y="17" text-anchor="middle" class="axis-label">{{ tick }}</text>
        </g>
      </g>

      <!-- one row per hit -->
      <g
        v-for="(hit, row) in shownHits"
        :key="hit.anchor"
        :transform="`translate(0, ${ROWS_Y + row * ROW_HEIGHT})`"
      >
        <a
          :href="`#${hit.anchor}`"
          @click.prevent="$emit('select', hit.anchor)"
        >
          <title>{{ hit.id }} {{ hit.description }}</title>
          <text x="0" y="9" class="hit-label">{{ shortId(hit.id) }}</text>
          <rect
            v-for="(hsp, i) in hit.hsps"
            :key="i"
            :x="hspBox(hsp).x"
            y="1"
            :width="hspBox(hsp).width"
            :height="ROW_HEIGHT - 4"
            :fill="scoreColor(hsp.bits)"
          >
            <title>
              {{ hit.id }}: {{ hsp.bits }} bits, E = {{ hsp.evalue }}, query
              {{ hsp.qStart }}-{{ hsp.qEnd }}
            </title>
          </rect>
        </a>
      </g>
    </svg>
    <button
      v-if="query.hits.length > DEFAULT_ROWS"
      type="button"
      class="btn btn-link btn-sm p-0"
      @click="expanded = !expanded"
    >
      {{ expanded ? "Show fewer hits" : `Show all ${query.hits.length} hits` }}
    </button>
  </figure>
</template>

<script>
// Hit map of a BLAST query: the query as a ruler and one row per hit, each
// HSP drawn over the query range it aligns to and coloured by score with
// the NCBI colour key. Replaces the GD image of the v2 HtmlMap.pl.
const SCORE_BINS = [
  { min: 200, color: "#e8302e", label: "≥ 200" },
  { min: 80, color: "#d43fd8", label: "80 - 200" },
  { min: 50, color: "#3f9f3d", label: "50 - 80" },
  { min: 40, color: "#1f5bd1", label: "40 - 50" },
  { min: -Infinity, color: "#222222", label: "< 40" },
];

export default {
  name: "BlastHitGraphic",
  props: {
    // { title, length, totalHits, hits: [{ id, anchor, description,
    //   hsps: [{ bits, evalue, qStart, qEnd }] }] }
    query: { type: Object, required: true },
  },
  data: () => ({
    expanded: false,
    WIDTH: 760,
    PLOT_X: 170,
    PLOT_WIDTH: 570,
    RULER_Y: 24,
    ROWS_Y: 66,
    ROW_HEIGHT: 14,
    DEFAULT_ROWS: 50,
    SCORE_BINS,
  }),
  computed: {
    shownHits() {
      return this.expanded
        ? this.query.hits
        : this.query.hits.slice(0, this.DEFAULT_ROWS);
    },
    queryLength() {
      const fromHits = Math.max(
        1,
        ...this.query.hits.flatMap((h) => h.hsps.map((s) => s.qEnd))
      );
      return Math.max(this.query.length || 0, fromHits);
    },
    height() {
      return this.ROWS_Y + this.shownHits.length * this.ROW_HEIGHT + 4;
    },
    ticks() {
      const len = this.queryLength;
      const raw = len / 5;
      const mag = 10 ** Math.floor(Math.log10(raw));
      const step = Math.max(
        1,
        [1, 2, 5, 10].map((m) => m * mag).find((s) => s >= raw)
      );
      const ticks = [1];
      for (let t = step; t < len; t += step) ticks.push(t);
      if (len - ticks[ticks.length - 1] > step / 3) ticks.push(len);
      return ticks;
    },
  },
  methods: {
    x(position) {
      return (
        this.PLOT_X + ((position - 1) / this.queryLength) * this.PLOT_WIDTH
      );
    },
    // Bar for an HSP covering qStart..qEnd inclusive, at least 2 px wide and
    // shifted left if that minimum would cross the end of the plot.
    hspBox(hsp) {
      const width = Math.max(2, this.x(hsp.qEnd + 1) - this.x(hsp.qStart));
      const x = Math.min(
        this.x(hsp.qStart),
        this.PLOT_X + this.PLOT_WIDTH - width
      );
      return { x, width };
    },
    scoreColor(bits) {
      return SCORE_BINS.find((b) => bits >= b.min).color;
    },
    shortId(id) {
      return id.length > 24 ? `${id.slice(0, 23)}…` : id;
    },
  },
};
</script>

<style scoped>
.blast-graphic svg {
  height: auto;
  font-family: sans-serif;
}
.axis-label {
  font-size: 11px;
  fill: #333;
}
.hit-label {
  font-size: 10px;
  fill: #1f5bd1;
}
a:focus .hit-label,
a:hover .hit-label {
  text-decoration: underline;
}
</style>
